import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import {
  Download,
  FileText,
  User,
  Search,
  Shield,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Clock,
  Eye,
  Trash2,
  Globe,
  Lock,
  ChevronRight
} from 'lucide-react';

const EXPORT_TYPES = [
  {
    id: 'user_data_portability',
    label: 'Portabilité des Données Utilisateur',
    description: 'Export complet des données personnelles d\'un utilisateur (Art. 20 RGPD)',
    icon: User,
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
    tables: ['user_profiles', 'appointments', 'property_inquiries', 'user_journey_events', 'login_history'],
  },
  {
    id: 'right_to_erasure',
    label: 'Droit à l\'Effacement',
    description: 'Rapport d\'anonymisation/suppression des données personnelles (Art. 17 RGPD)',
    icon: Trash2,
    color: 'bg-red-50 border-red-200',
    iconColor: 'text-red-600',
    tables: ['user_profiles', 'activity_logs', 'login_history', 'login_sessions'],
  },
  {
    id: 'processing_register',
    label: 'Registre des Traitements',
    description: 'Inventaire des activités de traitement (Art. 30 RGPD / Loi ivoirienne n°2013-450)',
    icon: FileText,
    color: 'bg-purple-50 border-purple-200',
    iconColor: 'text-purple-600',
    tables: ['data_archival_policies', 'activity_logs'],
  },
  {
    id: 'consent_audit',
    label: 'Audit des Consentements',
    description: 'Historique des consentements et préférences de communication',
    icon: Shield,
    color: 'bg-green-50 border-green-200',
    iconColor: 'text-green-600',
    tables: ['email_unsubscribe_preferences', 'sms_confirmation_preferences', 'alert_preferences'],
  },
  {
    id: 'security_incidents',
    label: 'Incidents de Sécurité',
    description: 'Rapport des accès suspects et violations potentielles (Art. 33 RGPD)',
    icon: Lock,
    color: 'bg-orange-50 border-orange-200',
    iconColor: 'text-orange-600',
    tables: ['login_history', 'login_sessions', 'trusted_devices'],
  },
  {
    id: 'full_platform_audit',
    label: 'Audit Complet Plateforme',
    description: 'Export global de toutes les transactions et activités de la plateforme',
    icon: Globe,
    color: 'bg-gray-50 border-gray-200',
    iconColor: 'text-gray-600',
    tables: ['activity_logs', 'user_journey_events', 'escalation_logs'],
  },
];

const GDPRComplianceExports = () => {
  const [selectedType, setSelectedType] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportHistory, setExportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchExportHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const { data, error: fetchError } = await supabase
        ?.from('compliance_export_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(20);
      if (fetchError) throw fetchError;
      setExportHistory(data || []);
    } catch (err) {
      console.error('Error fetching export history:', err?.message);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchExportHistory();
  }, [fetchExportHistory]);

  const handleUserSearch = async (query) => {
    setUserSearch(query);
    if (query?.length < 2) { setSearchResults([]); return; }
    try {
      setSearching(true);
      const { data, error: searchError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, role, created_at')
        ?.or(`full_name.ilike.%${query}%,email.ilike.%${query}%`)
        ?.limit(8);
      if (searchError) throw searchError;
      setSearchResults(data || []);
    } catch (err) {
      setError('Erreur de recherche: ' + err?.message);
    } finally {
      setSearching(false);
    }
  };

  const buildExportData = async (type, userId) => {
    const exportData = {
      export_type: type?.id,
      export_label: type?.label,
      generated_at: new Date()?.toISOString(),
      platform: 'Kapital States',
      legal_framework: 'RGPD (UE) 2016/679 + Loi ivoirienne n°2013-450',
      user_id: userId || null,
      data: {},
    };

    for (const table of type?.tables) {
      try {
        let query = supabase?.from(table)?.select('*');
        if (userId && ['user_profiles', 'appointments', 'property_inquiries', 'user_journey_events', 'login_history', 'login_sessions', 'email_unsubscribe_preferences', 'sms_confirmation_preferences', 'alert_preferences', 'trusted_devices']?.includes(table)) {
          query = query?.eq('user_id', userId);
          if (table === 'user_profiles') query = supabase?.from(table)?.select('*')?.eq('id', userId);
        }
        const { data } = await query?.limit(500);
        exportData.data[table] = data || [];
      } catch (_) {
        exportData.data[table] = [];
      }
    }
    return exportData;
  };

  const handleExport = async () => {
    if (!selectedType) return;
    const needsUser = ['user_data_portability', 'right_to_erasure']?.includes(selectedType?.id);
    if (needsUser && !selectedUser) {
      setError('Veuillez sélectionner un utilisateur pour ce type d\'export.');
      return;
    }

    try {
      setExporting(true);
      setError(null);
      const exportData = await buildExportData(selectedType, selectedUser?.id);

      // Log the export
      await supabase?.from('compliance_export_logs')?.insert({
        export_type: selectedType?.id,
        export_label: selectedType?.label,
        user_id_exported: selectedUser?.id || null,
        user_email_exported: selectedUser?.email || null,
        tables_included: selectedType?.tables,
        exported_at: new Date()?.toISOString(),
        status: 'completed',
      });

      // Download as JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = `compliance_export_${selectedType?.id}_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      a.download = filename;
      a?.click();
      URL.revokeObjectURL(url);

      setSuccess(`Export "${selectedType?.label}" téléchargé avec succès.`);
      fetchExportHistory();
    } catch (err) {
      setError('Erreur lors de l\'export: ' + err?.message);
    } finally {
      setExporting(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <Shield className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Exports de Conformité RGPD / Ivoirien</h3>
          <p className="text-sm text-gray-500">Générez des exports réglementaires conformes au RGPD et à la loi n°2013-450</p>
        </div>
      </div>
      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Export Type Selection */}
        <div className="lg:col-span-2 space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Type d'Export</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EXPORT_TYPES?.map(type => {
              const TypeIcon = type?.icon;
              const isSelected = selectedType?.id === type?.id;
              return (
                <button
                  key={type?.id}
                  onClick={() => { setSelectedType(type); setSelectedUser(null); setUserSearch(''); setSearchResults([]); }}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : `border ${type?.color} hover:border-blue-300`}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-100' : 'bg-white'}`}>
                      <TypeIcon className={`w-4 h-4 ${isSelected ? 'text-blue-600' : type?.iconColor}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold leading-tight ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{type?.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{type?.description}</p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {type?.tables?.map(t => (
                        <span key={t} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">{t}</span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel: User Search + Export Action */}
        <div className="space-y-4">
          {/* User Search (conditional) */}
          {selectedType && ['user_data_portability', 'right_to_erasure']?.includes(selectedType?.id) && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Rechercher un Utilisateur</h4>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => handleUserSearch(e?.target?.value)}
                  placeholder="Nom ou email..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {searching && <p className="text-xs text-gray-400 mt-2">Recherche...</p>}
              {searchResults?.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                  {searchResults?.map(u => (
                    <button
                      key={u?.id}
                      onClick={() => { setSelectedUser(u); setSearchResults([]); setUserSearch(u?.email); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left border-b border-gray-100 last:border-0 transition-colors"
                    >
                      <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-700 text-xs font-bold">{u?.full_name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u?.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{u?.email}</p>
                      </div>
                      <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {selectedUser && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-blue-900 truncate">{selectedUser?.full_name}</p>
                    <p className="text-xs text-blue-600 truncate">{selectedUser?.email}</p>
                  </div>
                  <button onClick={() => { setSelectedUser(null); setUserSearch(''); }} className="ml-auto text-blue-400 hover:text-blue-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Export Button */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Générer l'Export</h4>
            {!selectedType ? (
              <p className="text-sm text-gray-400 text-center py-4">Sélectionnez un type d'export</p>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-600">Type sélectionné</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{selectedType?.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{selectedType?.tables?.length} table(s) incluse(s)</p>
                </div>
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {exporting ? 'Génération...' : 'Télécharger (JSON)'}
                </button>
                <p className="text-xs text-gray-400 text-center">Format JSON conforme RGPD</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Export History */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Historique des Exports</h4>
          <button onClick={fetchExportHistory} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {loadingHistory ? (
          <div className="space-y-2">
            {[...Array(3)]?.map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : exportHistory?.length === 0 ? (
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-6 text-center">
            <Eye className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Aucun export effectué pour l'instant</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {exportHistory?.map(log => (
                  <tr key={log?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{log?.export_label || log?.export_type}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{log?.user_email_exported || 'Plateforme entière'}</td>
                    <td className="px-4 py-3 text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(log?.exported_at || log?.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${log?.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {log?.status === 'completed' ? 'Complété' : log?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Fix missing X import
const X = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default GDPRComplianceExports;
